import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Chip } from '@mui/material';
import { isEqual, uniqBy } from 'lodash';
import { type KeyboardEvent, useEffect, useMemo, useRef } from 'react';

import SearchableSelect from '@/components/SearchableSelect';
import TimeFilter, {
  DEFAULT_TIME_FILTER,
  getCustomTimeFilterValues,
  type TimePresetKeyWithField
} from '@/components/TimeFilter';
import theme from '@/theme.ts';

import { useFilterLabels, useQueryParams } from './SearchBar.hooks';
import useStyles from './SearchBar.styles.ts';
import type { SearchBarProps } from './SearchBar.types';

const SearchBar = (props: SearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    searchableSelectFilters,
    onFilterChange,
    onTimeFilterChange,
    selectedFilters,
    enableFreeSearch = true,
    enableTimeFilter = false,
    disableTimeFilterType = false,
    searchbarInputContainerStyle = {},
    createdTimeLabel,
    lastUpdatedTimeLabel
  } = props;

  // Remove duplicate options from searchable select filters
  // This is done to ensure that the options are unique and do not cause any issues with the searchable select component
  // This is needed because adding in 0 count filter options can sometimes create duplicate options
  const uniqueSearchableSelectFilters = searchableSelectFilters.map(
    (filter) => {
      const uniqueFilterOptions = uniqBy(filter.filterOptions, 'value');
      return {
        ...filter,
        filterOptions: uniqueFilterOptions
      };
    }
  );

  const { filterLabels, getFilterOptionLabel } = useFilterLabels(
    uniqueSearchableSelectFilters
  );

  const {
    filters,
    timeFilter,
    addFilter,
    removeFilter,
    clearFilters,
    setTimeFilter
  } = useQueryParams();

  const { classes } = useStyles({ enableTimeFilter });

  useEffect(() => {
    if (!isEqual(selectedFilters, filters)) {
      onFilterChange?.(filters);
    }
    onTimeFilterChange?.(timeFilter as TimePresetKeyWithField);
  }, [
    filters,
    onFilterChange,
    onTimeFilterChange,
    selectedFilters,
    timeFilter
  ]);

  // Add custom time filter chip if the time filter is set to custom
  const customTimeFilterChip = useMemo(() => {
    if (!enableTimeFilter || !timeFilter.startsWith('custom')) return null;

    const { from, to } = getCustomTimeFilterValues(timeFilter);

    return (
      <Chip
        className={classes.searchbarFilterChip}
        size="small"
        label={
          <>
            Date:
            <Box sx={{ ml: 0.5, fontWeight: 700 }} component="span">
              {from || 'Any'} - {to || 'Now'}
            </Box>
          </>
        }
      />
    );
  }, [enableTimeFilter, timeFilter]);

  const clearButton = Object.keys(filters).length !== 0 && (
    <Button variant="text" onClick={clearFilters} size="small">
      Clear
    </Button>
  );

  const handleApply = (key: string, options: string[]) => {
    if (options.length === 0) {
      removeFilter(key);
    } else {
      addFilter(key, options);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (
      (target.value.trim() !== '' && e.code === 'Enter') ||
      (target.value.trim() !== '' && e.code === 'NumpadEnter')
    ) {
      addFilter('any', [target.value.trim()]);
      target.value = '';
    }
  };

  const handleChangeTimeFilter = (time: TimePresetKeyWithField) => {
    setTimeFilter(time);
    onTimeFilterChange?.(time);
  };

  const handleSearchButtonClick = () => {
    const searchValue = inputRef.current?.value.trim();
    if (inputRef.current && searchValue) {
      addFilter('any', [searchValue]);
      inputRef.current.value = '';
    }
  };

  return (
    <div className={classes.searchbar}>
      {enableFreeSearch && (
        <div className={classes.searchbarContainer}>
          <Box
            sx={{
              [theme.breakpoints.down('xxl')]: { width: '70%' },
              [theme.breakpoints.down('xl')]: { width: '70%' },
              [theme.breakpoints.down('lg')]: { width: '100%' },
              display: 'flex',
              minWidth: '33vw',
              ...searchbarInputContainerStyle // overrides from parent component
            }}
          >
            {enableTimeFilter && (
              <TimeFilter
                selectedTime={
                  (timeFilter as TimePresetKeyWithField) || DEFAULT_TIME_FILTER
                }
                onChange={handleChangeTimeFilter}
                disableTimeFilterType={disableTimeFilterType}
                createdTimeLabel={createdTimeLabel}
                lastUpdatedTimeLabel={lastUpdatedTimeLabel}
              />
            )}
            <div className={classes.searchbarInput}>
              {customTimeFilterChip}
              {Object.keys(filters).map((key) => (
                <Chip
                  className={classes.searchbarFilterChip}
                  size="small"
                  key={key}
                  label={
                    <>
                      {filterLabels[key]}:
                      <Box sx={{ ml: 0.5, fontWeight: 700 }} component="span">
                        {(filters as Record<string, string[]>)[key]
                          .map((val: string) => getFilterOptionLabel(key, val))
                          .join(', ')}
                      </Box>
                    </>
                  }
                  onDelete={() => removeFilter(key)}
                />
              ))}
              <Box className={classes.textInputWrapper}>
                <input
                  type="text"
                  name="searchTerm"
                  id="searchTerm"
                  ref={inputRef}
                  onKeyDown={handleKeyDown}
                />
              </Box>
              {clearButton}
            </div>
            <Button
              variant="outlined"
              color="secondary"
              className={classes.searchbarSearchBtn}
              onClick={handleSearchButtonClick}
            >
              <i className="fal fa-search" />
              <FontAwesomeIcon icon={faSearch} size="lg" />
            </Button>
          </Box>
        </div>
      )}
      {uniqueSearchableSelectFilters
        .filter((f) => !f.hidden)
        .map((filter) => (
          <SearchableSelect
            key={filter.field}
            field={filter.field}
            filterOptions={filter.filterOptions}
            title={filter.title}
            variant={filter.config.variant!}
            searchable={filter.config.searchable}
            showCounts={filter.config.showCounts}
            selectedOptions={
              filters[filter.field as keyof typeof filters] || []
            }
            onApply={handleApply}
          />
        ))}
    </div>
  );
};

export default SearchBar;
